import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor } from '@tiptap/react';
import { ArrowUp } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { inlineEditBarMessages as messages } from './inline-edit-bar.messages';

// Mirrors the floating Ask Heidi input's ProseMirror styling (vertically
// centred placeholder, no outline). Height and text size come from `density`.
const EDITOR_CLASS =
  'w-full min-w-0 [&_.ProseMirror]:min-w-0 [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:break-words [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child]:relative [&_.ProseMirror_p.is-editor-empty:first-child]:w-full [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:absolute [&_.ProseMirror_p.is-editor-empty:first-child::before]:left-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:right-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:top-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:truncate [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-text-tertiary [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]';

type PromptInputDensity = 'default' | 'compact';

// Compact grows to two lines then scrolls, with an `xs` send button so the
// row height doesn't jump on the first keystroke.
const DENSITY_STYLES: Record<
  PromptInputDensity,
  { text: string; minHeight: string; maxHeight: string; sendButtonSize: 'xs' | 'sm' }
> = {
  default: {
    text: 'text-base',
    minHeight: 'min-h-[32px]',
    maxHeight: '[&_.ProseMirror]:max-h-[200px]',
    sendButtonSize: 'sm',
  },
  compact: {
    text: 'text-sm',
    minHeight: 'min-h-[26px]',
    maxHeight: '[&_.ProseMirror]:max-h-[2.5rem]',
    sendButtonSize: 'xs',
  },
};

/**
 * Lite free-form prompt for the highlighted text: full-width input with
 * placeholder and send. Dictation is not wired here.
 */
export const InlineEditPromptInput = ({
  onSubmit,
  placeholder,
  density = 'default',
}: {
  onSubmit: (question: string) => void;
  /** Overrides the default "What would you like to change?" placeholder. */
  placeholder?: string;
  density?: PromptInputDensity;
}) => {
  const intl = useIntl();
  const [hasText, setHasText] = useState(false);
  const styles = DENSITY_STYLES[density];

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      Placeholder.configure({
        placeholder: placeholder ?? intl.formatMessage(messages.promptPlaceholder),
        showOnlyWhenEditable: true,
      }),
    ],
    editorProps: {
      attributes: {
        class: cn('w-full min-w-0 text-text-primary outline-none', styles.text),
      },
    },
    onUpdate: ({ editor: e }) => setHasText(e.getText().trim().length > 0),
    autofocus: 'end',
    immediatelyRender: false,
  });

  const submit = () => {
    const question = editor?.getText().trim();
    if (question) onSubmit(question);
  };

  return (
    <div
      className="flex w-full items-center gap-2"
      // Allow default mousedown behaviour so the editor can take focus (the
      // bar's root prevents it to protect the note selection).
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          submit();
        }
      }}
      data-testid="inline-edit-prompt-input"
    >
      <div className={cn('flex min-w-0 flex-1 items-center', styles.minHeight)}>
        <EditorContent editor={editor} className={cn(EDITOR_CLASS, styles.maxHeight)} />
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <AnimatePresence mode="popLayout" initial={false}>
          {hasText ? (
            <m.div
              key="send"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <Button
                type="button"
                variant="default"
                size={styles.sendButtonSize}
                iconOnly
                onClick={submit}
                aria-label={intl.formatMessage(messages.send)}
                data-testid="inline-edit-prompt-send"
                className="bg-background-primary text-text-primary-invert"
              >
                <ArrowUp size={12} strokeWidth={2.5} />
              </Button>
            </m.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
};
